/**
 * PhotoGallery — thumbnail grid + upload button + lightbox modal.
 *
 * Used by AdminInventoryDetailPage for both:
 *   - the BASIS-section gallery on inventory items
 *   - per-damage-row strips on damage entries
 *
 * Props:
 *   photos        : array of { id, thumb_url, original_url, caption,
 *                              width, height, mime_type }
 *   onUpload(filesArray)  : called when user selects one or more files
 *   onDelete(photoId)     : called when user confirms delete in lightbox
 *   onCaptionSave(id, c)  : optional; called from lightbox caption edit
 *   busy                  : boolean — disables upload + delete UI when true
 *   uploading             : boolean — shows progress chip on upload button
 *   maxPhotos             : soft cap; hides upload button when reached
 *   compact               : true → smaller thumbnails (used inside damage rows)
 *
 * The component is locale-agnostic except for German labels matching the
 * rest of the inventory page ("Foto hinzufügen", "Löschen", etc.).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";

const ACCEPT =
  "image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif";

const MAX_FILES_PER_REQUEST = 5;
const MAX_FILE_SIZE_BYTES = 15 * 1024 * 1024;

function normalizePhotoCount(photos) {
  return Array.isArray(photos) ? photos.length : 0;
}

function CaptionDisplay({ caption }) {
  if (!caption) return null;
  return (
    <p className="mt-1 line-clamp-2 break-words text-[11px] font-medium leading-snug text-[#94a3b8]">
      {caption}
    </p>
  );
}

function ThumbButton({ photo, onClick, compact, size = null, className = "" }) {
  /* S9: the article gallery's tile drops from 140x110 to 112x84. The Fotos
     card is a full-width strip now, and at that width the old tile made the
     card taller than its content deserved. Thumbnails only — still thumb_url,
     still no original_url, still capped by maxPhotos. */
  const w = size ? size.w : compact ? "w-[88px]" : "w-[112px]";
  const h = size ? size.h : compact ? "h-[88px]" : "h-[84px]";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative shrink-0 overflow-hidden rounded-[8px] border border-[#1e2130] bg-[#0d0f14] ${w} ${h} hover:border-[#3a3f55] focus:outline-none focus:ring-2 focus:ring-[#5b8cff]/40 ${className}`}
      aria-label={photo.caption ? `Foto: ${photo.caption}` : "Foto öffnen"}
    >
      <img
        src={photo.thumb_url}
        alt={photo.caption || ""}
        className="h-full w-full object-cover"
        loading="lazy"
      />
    </button>
  );
}

function UploadButton({
  onPickFiles,
  uploading,
  disabled,
  compact,
}) {
  const inputRef = useRef(null);
  /* Same box as ThumbButton, so the upload tile sits in the row rather than
     next to it. */
  const w = compact ? "w-[88px]" : "w-[112px]";
  const h = compact ? "h-[88px]" : "h-[84px]";

  function handleChange(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    onPickFiles(files);
    // Reset so the same file can be picked again later.
    e.target.value = "";
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple
        onChange={handleChange}
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || uploading}
        className={`flex flex-col items-center justify-center rounded-[8px] border border-dashed border-[#3a3f55] bg-[#0d0f14] text-[#94a3b8] hover:border-[#5b8cff] hover:text-[#c5cbe0] disabled:cursor-not-allowed disabled:opacity-40 ${w} ${h}`}
        aria-label="Foto hinzufügen"
      >
        {uploading ? (
          <span className="text-[12px] font-medium">Lädt …</span>
        ) : (
          <>
            <span className="text-[24px] leading-none">＋</span>
            <span className="mt-1 text-[11px] font-medium">
              {compact ? "Foto" : "Foto hinzufügen"}
            </span>
          </>
        )}
      </button>
    </>
  );
}

function Lightbox({
  photos,
  startIndex,
  onClose,
  onDelete,
  onCaptionSave,
  busy,
}) {
  const [index, setIndex] = useState(startIndex);
  const [captionDraft, setCaptionDraft] = useState("");
  const [editingCaption, setEditingCaption] = useState(false);

  const photo = photos[index];

  // Reset caption draft when navigating between photos.
  useEffect(() => {
    setEditingCaption(false);
    setCaptionDraft(photo?.caption || "");
  }, [photo?.id, photo?.caption]);

  const goPrev = useCallback(() => {
    setIndex((i) => (i === 0 ? photos.length - 1 : i - 1));
  }, [photos.length]);

  const goNext = useCallback(() => {
    setIndex((i) => (i === photos.length - 1 ? 0 : i + 1));
  }, [photos.length]);

  // Keyboard navigation: Esc, arrow keys.
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, goPrev, goNext]);

  if (!photo) return null;

  const showNav = photos.length > 1;

  function handleDelete() {
    if (!window.confirm("Foto wirklich löschen?")) return;
    onDelete(photo.id);
  }

  function handleSaveCaption() {
    const trimmed = captionDraft.trim();
    onCaptionSave?.(photo.id, trimmed || null);
    setEditingCaption(false);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Foto-Ansicht"
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-black/85 p-4"
      onClick={onClose}
    >
      <div
        className="relative flex w-full max-w-5xl flex-col items-center gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={photo.original_url}
          alt={photo.caption || ""}
          className="max-h-[78vh] w-auto max-w-full rounded-[10px] border border-[#1e2130] bg-[#0d0f14] object-contain"
        />

        <div className="flex w-full flex-wrap items-center justify-between gap-3 rounded-[10px] border border-[#1e2130] bg-[#13161f] px-4 py-3">
          <div className="min-w-0 flex-1">
            {editingCaption ? (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  value={captionDraft}
                  onChange={(e) => setCaptionDraft(e.target.value)}
                  maxLength={200}
                  placeholder="Beschreibung (optional)"
                  className="min-w-[220px] flex-1 rounded-[8px] border border-[#1e2130] bg-[#0d0f14] px-3 py-2 text-[13px] font-medium text-[#c5cbe0] outline-none focus:border-[#3a3f55]"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleSaveCaption}
                  className="rounded-[8px] bg-[#5b8cff] px-3 py-2 text-[12px] font-semibold text-white hover:bg-[#4a7ae8]"
                >
                  Speichern
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingCaption(false);
                    setCaptionDraft(photo.caption || "");
                  }}
                  className="rounded-[8px] border border-[#1e2130] bg-[#0d0f14] px-3 py-2 text-[12px] font-medium text-[#94a3b8] hover:border-[#3a3f55]"
                >
                  Abbrechen
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => onCaptionSave && setEditingCaption(true)}
                className="block w-full text-left text-[13px] font-medium text-[#c5cbe0] hover:text-[#94a3b8]"
                aria-label="Beschreibung bearbeiten"
                disabled={!onCaptionSave}
              >
                {photo.caption || (
                  <span className="text-[#4a5070]">
                    {onCaptionSave
                      ? "Beschreibung hinzufügen …"
                      : "Keine Beschreibung"}
                  </span>
                )}
              </button>
            )}
            <div className="mt-1 text-[11px] tabular-nums text-[#4a5070]">
              {photo.width}×{photo.height}
              {showNav ? ` · ${index + 1} / ${photos.length}` : ""}
            </div>
          </div>

          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={handleDelete}
              disabled={busy}
              className="rounded-[8px] border border-[#3a1f24] bg-[#1e0d10] px-3 py-2 text-[12px] font-semibold text-[#ff6b6b] hover:border-[#ff6b6b] disabled:opacity-50"
            >
              Löschen
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-[8px] border border-[#1e2130] bg-[#13161f] px-3 py-2 text-[12px] font-semibold text-[#c5cbe0] hover:border-[#3a3f55]"
            >
              Schliessen
            </button>
          </div>
        </div>

        {showNav ? (
          <>
            <button
              type="button"
              onClick={goPrev}
              aria-label="Vorheriges Foto"
              className="absolute left-2 top-[40%] -translate-y-1/2 rounded-full border border-[#1e2130] bg-[#13161f]/90 px-3 py-2 text-[#c5cbe0] hover:border-[#3a3f55]"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={goNext}
              aria-label="Nächstes Foto"
              className="absolute right-2 top-[40%] -translate-y-1/2 rounded-full border border-[#1e2130] bg-[#13161f]/90 px-3 py-2 text-[#c5cbe0] hover:border-[#3a3f55]"
            >
              ›
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}

export default function PhotoGallery({
  photos,
  onUpload,
  onDelete,
  onCaptionSave,
  busy = false,
  uploading = false,
  maxPhotos,
  compact = false,
  emptyHint = "Noch keine Fotos.",
}) {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const list = Array.isArray(photos) ? photos : [];
  const count = normalizePhotoCount(photos);
  const atCap =
    typeof maxPhotos === "number" && count >= maxPhotos;

  function handlePickFiles(files) {
    const slice = files.slice(0, MAX_FILES_PER_REQUEST);
    // Client-side size check matches backend (HTTP 413 is the canonical fail
    // path, but rejecting here gives the user instant feedback).
    const tooBig = slice.find((f) => f.size > MAX_FILE_SIZE_BYTES);
    if (tooBig) {
      window.alert(
        `Datei "${tooBig.name}" ist zu gross (max. ${MAX_FILE_SIZE_BYTES / (1024 * 1024)} MB).`
      );
      return;
    }
    if (files.length > MAX_FILES_PER_REQUEST) {
      window.alert(
        `Maximal ${MAX_FILES_PER_REQUEST} Dateien pro Upload. Es werden die ersten ${MAX_FILES_PER_REQUEST} hochgeladen.`
      );
    }
    onUpload(slice);
  }

  function handleDelete(photoId) {
    onDelete(photoId);
    setLightboxIndex(null);
  }

  return (
    <div>
      {count === 0 ? (
        <div className="flex items-center gap-3">
          <p className="text-[12px] font-medium text-[#4a5070]">{emptyHint}</p>
          <UploadButton
            onPickFiles={handlePickFiles}
            uploading={uploading}
            disabled={busy || atCap}
            compact={compact}
          />
        </div>
      ) : (
        <div className="flex flex-wrap gap-3">
          {list.map((photo, idx) => (
            <ThumbButton
              key={photo.id}
              photo={photo}
              compact={compact}
              onClick={() => setLightboxIndex(idx)}
            />
          ))}
          {atCap ? null : (
            <UploadButton
              onPickFiles={handlePickFiles}
              uploading={uploading}
              disabled={busy}
              compact={compact}
            />
          )}
        </div>
      )}

      {typeof maxPhotos === "number" && count > 0 ? (
        <p className="mt-2 text-[11px] tabular-nums text-[#4a5070]">
          {count} / {maxPhotos} Fotos
        </p>
      ) : null}

      {lightboxIndex !== null && list[lightboxIndex] ? (
        <Lightbox
          photos={list}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onDelete={handleDelete}
          onCaptionSave={onCaptionSave}
          busy={busy}
        />
      ) : null}
    </div>
  );
}
